import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import useAuthenticatedAxios from '../../../auth/AuthenticatedAxios';

/**
 * Client Profile Panel
 *
 * Shows ALL data the platform holds about a client (from the shared UserFormData
 * store — collected via forms, AI chat, or widget conversation analysis) grouped
 * by category, with inline validated editing. Edits are validated server-side
 * against the form format (dropdown answers must be a valid option), so whatever
 * the adviser saves stays fillable into the actual IRCC forms.
 */

const Wrap = styled.div`
  background: rgba(17, 24, 39, 0.5);
  border: 1px solid rgba(75, 85, 99, 0.25);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
`;
const Head = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;
`;
const Title = styled.div`font-size: 15px; font-weight: 700; color: #e5e7eb;`;
const Sub = styled.div`font-size: 12px; color: #9ca3af;`;
const GroupTitle = styled.div`
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  color: #a5b4fc; margin: 14px 0 6px;
`;
const Row = styled.div`
  display: grid; grid-template-columns: 42% 1fr; gap: 10px; align-items: center;
  padding: 6px 0; border-bottom: 1px solid rgba(75,85,99,0.15);
`;
const Label = styled.div`font-size: 12px; color: #cbd5e1; display: flex; align-items: center; gap: 6px;`;
const RmBtn = styled.button`
  border: none; background: transparent; cursor: pointer; color: #6b7280;
  font-size: 14px; line-height: 1; padding: 0 2px; border-radius: 4px;
  &:hover { color: #f87171; background: rgba(248,113,113,0.12); }
`;
const CustomTag = styled.span`
  font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  color: #818cf8; background: rgba(99,102,241,0.15); border-radius: 4px; padding: 1px 5px;
`;
const UsedBy = styled.div`
  font-size: 10px; color: #6b7280; margin-top: 2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const ValueBox = styled.div`display: flex; flex-direction: column; gap: 3px;`;
const Input = styled.input`
  background: rgba(31,41,55,0.6); border: 1px solid ${p => p.$err ? '#ef4444' : 'rgba(75,85,99,0.4)'};
  border-radius: 6px; padding: 6px 8px; color: #e5e7eb; font-size: 12px; width: 100%;
  &:focus { outline: none; border-color: #6366f1; }
`;
const Err = styled.div`font-size: 11px; color: #f87171;`;
const Saved = styled.span`font-size: 11px; color: #34d399; margin-left: 6px;`;
const Empty = styled.div`color: #6b7280; font-size: 13px; padding: 16px 0; text-align: center;`;

// ---- Add-field UI ----
const AddBar = styled.div`display: flex; justify-content: center; margin-top: 14px;`;
const AddBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px; cursor: pointer;
  background: transparent; border: 1px dashed rgba(99,102,241,0.5);
  color: #a5b4fc; border-radius: 8px; padding: 7px 14px; font-size: 12px; font-weight: 600;
  &:hover { background: rgba(99,102,241,0.12); border-color: #6366f1; }
`;
const AddPanel = styled.div`
  margin-top: 12px; padding: 12px; border-radius: 10px;
  background: rgba(99,102,241,0.08); border: 1px solid rgba(99,102,241,0.3);
`;
const AddSearch = styled.input`
  width: 100%; box-sizing: border-box; background: rgba(15,23,42,0.7);
  border: 1px solid rgba(75,85,99,0.4); border-radius: 6px; padding: 7px 9px;
  color: #e5e7eb; font-size: 12px; margin-bottom: 8px;
  &:focus { outline: none; border-color: #6366f1; }
`;
const AddList = styled.div`
  max-height: 220px; overflow-y: auto; display: flex; flex-direction: column; gap: 2px;
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.4); border-radius: 4px; }
`;
const AddItem = styled.div`
  padding: 7px 9px; border-radius: 6px; cursor: pointer; font-size: 12px; color: #cbd5e1;
  display: flex; justify-content: space-between; gap: 8px; align-items: center;
  &:hover { background: rgba(99,102,241,0.2); color: #fff; }
  span.cat { font-size: 10px; color: #818cf8; flex: none; }
`;
const AddDivider = styled.div`height: 1px; background: rgba(148,163,184,0.2); margin: 8px 0;`;
const CustomRow = styled.div`display: flex; gap: 6px;`;
const MiniBtn = styled.button`
  cursor: pointer; border: none; border-radius: 6px; padding: 7px 12px; font-size: 12px; font-weight: 600;
  background: ${p => p.$primary ? 'linear-gradient(135deg,#6366f1,#7c73f0)' : 'rgba(51,65,85,0.7)'};
  color: #fff; white-space: nowrap;
  &:hover { filter: brightness(1.1); }
`;

// ---- Dark, searchable dropdown (replaces the native <select>) ----
const CbWrap = styled.div`position: relative; width: 100%;`;
const CbButton = styled.button`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  width: 100%; text-align: left; cursor: pointer;
  background: rgba(31,41,55,0.6);
  border: 1px solid ${p => p.$err ? '#ef4444' : 'rgba(75,85,99,0.4)'};
  border-radius: 6px; padding: 6px 8px; color: #e5e7eb; font-size: 12px;
  &:hover { border-color: rgba(99,102,241,0.6); }
  &:focus { outline: none; border-color: #6366f1; }
`;
const CbValue = styled.span`
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  color: ${p => p.$placeholder ? '#6b7280' : '#e5e7eb'};
`;
const CbCaret = styled.span`color: #9ca3af; font-size: 10px; flex: none;`;
const CbPop = styled.div`
  position: absolute; z-index: 50; top: calc(100% + 4px); left: 0; right: 0;
  background: #111827; border: 1px solid rgba(99,102,241,0.4);
  border-radius: 8px; box-shadow: 0 12px 28px rgba(0,0,0,0.5);
  overflow: hidden;
`;
const CbSearch = styled.input`
  width: 100%; box-sizing: border-box; border: none; border-bottom: 1px solid rgba(75,85,99,0.4);
  background: #0b1220; color: #e5e7eb; font-size: 12px; padding: 8px 10px;
  &:focus { outline: none; }
`;
const CbList = styled.div`max-height: 240px; overflow-y: auto;
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.4); border-radius: 4px; }
`;
const CbItem = styled.div`
  padding: 8px 10px; font-size: 12px; cursor: pointer;
  color: ${p => p.$active ? '#c7d2fe' : '#cbd5e1'};
  background: ${p => p.$active ? 'rgba(99,102,241,0.18)' : 'transparent'};
  &:hover { background: rgba(99,102,241,0.25); color: #fff; }
`;
const CbEmpty = styled.div`padding: 10px; font-size: 12px; color: #6b7280; text-align: center;`;

const ComboSelect = ({ value, options, onChange, error }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  const selected = options.find(o => String(o.value) === String(value));
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter(o =>
      String(o.label).toLowerCase().includes(s) || String(o.value).toLowerCase().includes(s));
  }, [q, options]);

  useEffect(() => {
    if (!open) return;
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    setTimeout(() => searchRef.current?.focus(), 0);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const pick = (v) => { setOpen(false); setQ(''); onChange(v); };

  return (
    <CbWrap ref={ref}>
      <CbButton type="button" $err={error} onClick={() => setOpen(o => !o)}>
        <CbValue $placeholder={!selected}>{selected ? selected.label : '— not set —'}</CbValue>
        <CbCaret>▼</CbCaret>
      </CbButton>
      {open && (
        <CbPop>
          {options.length > 6 && (
            <CbSearch ref={searchRef} placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} />
          )}
          <CbList>
            <CbItem $active={!selected} onClick={() => pick('')}>— not set —</CbItem>
            {filtered.map(o => (
              <CbItem key={o.value} $active={String(o.value) === String(value)} onClick={() => pick(o.value)}>
                {o.label}
              </CbItem>
            ))}
            {filtered.length === 0 && <CbEmpty>No matches</CbEmpty>}
          </CbList>
        </CbPop>
      )}
    </CbWrap>
  );
};

const CATEGORY_LABELS = {
  personalInfo: 'Personal',
  contact: 'Contact',
  passport: 'Passport',
  nationalId: 'National ID',
  employment: 'Employment',
  education: 'Education',
  immigration: 'Immigration',
  languages: 'Languages',
  prCard: 'PR Card',
  portal: 'Portal',
  custom: 'Custom',
  other: 'Other',
};

const slugify = (s) =>
  s.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
    .replace(/\s+(.)/g, (_, c) => c.toUpperCase()).replace(/\s/g, '');

function deriveEmail(selectedUser, userEmail) {
  if (userEmail) return userEmail;
  const u = selectedUser || {};
  const direct = u.email || u.userEmail || u.mail || u.customerEmail;
  if (direct) return direct;
  if (Array.isArray(u.users)) {
    const withEmail = u.users.find(x => (x.name || '').includes('@'));
    if (withEmail) return withEmail.name;
  }
  if ((u.name || '').includes('@')) return u.name;
  return null;
}

const ClientProfilePanel = ({ selectedUser, userEmail }) => {
  const axios = useAuthenticatedAxios();
  const email = deriveEmail(selectedUser, userEmail);
  const conversationId = selectedUser?._id || selectedUser?.threadId || null;
  // We can look the client up by email OR by conversation — whichever we have.
  // The server merges every identifier tied to the client, so the profile shows
  // all their data even if a throwaway email was used during the chat.
  const handle = email || conversationId;
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});     // key -> in-progress value
  const [errors, setErrors] = useState({});     // key -> error text
  const [savedKey, setSavedKey] = useState(null);
  // Where edits are persisted (a real email if known, else lead:{conversationId}) —
  // provided by the server so writes land on the canonical record.
  const [writeId, setWriteId] = useState(null);
  // Keys the adviser chose to reveal (empty form fields they want to fill).
  const [revealed, setRevealed] = useState(() => new Set());
  // Locally-created custom fields (persist to server on save).
  const [customFields, setCustomFields] = useState([]);
  const [adding, setAdding] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [customLabel, setCustomLabel] = useState('');

  const load = useCallback(async () => {
    if (!handle) return;
    setLoading(true);
    try {
      const res = await axios.get(`/form-filling/client-profile/${encodeURIComponent(handle)}`, {
        params: { _t: Date.now(), ...(conversationId ? { conversationId } : {}) },
      });
      setFields(res.data?.fields || []);
      setWriteId(res.data?.writeIdentifier || handle);
    } catch (e) {
      console.error('[ClientProfile] load error', e);
    } finally {
      setLoading(false);
    }
  }, [axios, handle, conversationId]);

  useEffect(() => { load(); }, [load]);

  const save = async (field, value) => {
    setErrors(prev => ({ ...prev, [field.key]: null }));
    try {
      const target = writeId || handle;
      const res = await axios.put(`/form-filling/client-profile/${encodeURIComponent(target)}`, { key: field.key, value, conversationId });
      const finalVal = res.data?.value ?? value;
      setFields(prev => prev.map(f => f.key === field.key ? { ...f, value: finalVal } : f));
      setCustomFields(prev => prev.map(f => f.key === field.key ? { ...f, value: finalVal } : f));
      setDrafts(prev => ({ ...prev, [field.key]: undefined }));
      setSavedKey(field.key);
      setTimeout(() => setSavedKey(k => (k === field.key ? null : k)), 1500);
    } catch (e) {
      const msg = e?.response?.data?.error || 'Could not save (invalid for the form).';
      setErrors(prev => ({ ...prev, [field.key]: msg }));
    }
  };

  const removeField = async (field) => {
    // Custom fields (or any field holding a value) are unset on the server; a
    // revealed-but-empty form field is just hidden locally.
    const isCustom = field.category === 'custom' || !field.known;
    const hadValue = field.value != null && String(field.value).trim() !== '';
    if (isCustom || hadValue) {
      try {
        await axios.delete(`/form-filling/client-profile/${encodeURIComponent(writeId || handle)}/field`,
          { data: { key: field.key, conversationId } });
      } catch (e) {
        setErrors(prev => ({ ...prev, [field.key]: e?.response?.data?.error || 'Could not remove.' }));
        return;
      }
    }
    setFields(prev => prev.filter(f => f.key !== field.key));
    setCustomFields(prev => prev.filter(f => f.key !== field.key));
    setDrafts(prev => ({ ...prev, [field.key]: undefined }));
    setRevealed(prev => { const n = new Set(prev); n.delete(field.key); return n; });
  };

  const addKnownField = (key) => {
    setRevealed(prev => new Set(prev).add(key));
    setAdding(false); setAddSearch('');
  };
  const createCustomField = () => {
    const label = customLabel.trim();
    const s = slugify(label);
    if (!s) return;
    const key = `custom.${s}`;
    if (!customFields.some(f => f.key === key) && !fields.some(f => f.key === key)) {
      setCustomFields(prev => [...prev, {
        key, label, value: '', type: 'text', options: [], usedBy: [], category: 'custom', known: false,
      }]);
    }
    setRevealed(prev => new Set(prev).add(key));
    setCustomLabel(''); setAdding(false);
  };

  if (!handle) return null;

  // Merge server fields with any locally-created custom fields.
  const allFields = [...fields, ...customFields.filter(cf => !fields.some(f => f.key === cf.key))];
  const hasValue = (f) => f.value != null && String(f.value).trim() !== '';
  // Show fields that have data, or that the adviser explicitly added/created.
  const visible = allFields.filter(f => hasValue(f) || revealed.has(f.key) || f.category === 'custom');
  // Catalog for the "Add field" picker: known form fields not already shown.
  const availableToAdd = allFields
    .filter(f => f.known && !hasValue(f) && !revealed.has(f.key))
    .filter(f => !addSearch.trim() || (f.label + ' ' + f.key).toLowerCase().includes(addSearch.trim().toLowerCase()));

  const groups = visible.reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f);
    return acc;
  }, {});
  const orderedCats = Object.keys(CATEGORY_LABELS).filter(c => groups[c]).concat(
    Object.keys(groups).filter(c => !CATEGORY_LABELS[c])
  );

  const addFieldUI = (
    <>
      <AddBar>
        <AddBtn onClick={() => setAdding(a => !a)}>{adding ? '× Close' : '＋ Add field'}</AddBtn>
      </AddBar>
      {adding && (
        <AddPanel>
          <AddSearch
            autoFocus
            placeholder="Search form fields (e.g. passport, city)…"
            value={addSearch}
            onChange={e => setAddSearch(e.target.value)}
          />
          <AddList>
            {availableToAdd.length === 0 ? (
              <Empty>No more form fields{addSearch ? ' match' : ''}.</Empty>
            ) : availableToAdd.slice(0, 60).map(f => (
              <AddItem key={f.key} onClick={() => addKnownField(f.key)}>
                <span>{f.label}</span>
                <span className="cat">{CATEGORY_LABELS[f.category] || f.category}</span>
              </AddItem>
            ))}
          </AddList>
          <AddDivider />
          <CustomRow>
            <AddSearch
              style={{ marginBottom: 0 }}
              placeholder="…or type a custom field name"
              value={customLabel}
              onChange={e => setCustomLabel(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') createCustomField(); }}
            />
            <MiniBtn $primary onClick={createCustomField}>Add custom</MiniBtn>
          </CustomRow>
          <Sub style={{ marginTop: 6 }}>Custom fields are notes — they aren't auto-filled into forms.</Sub>
        </AddPanel>
      )}
    </>
  );

  return (
    <Wrap>
      <Head>
        <div>
          <Title>Client Profile</Title>
          <Sub>{email || 'this client'} · editable, validated to the form format</Sub>
        </div>
        <Sub>{visible.length} field(s)</Sub>
      </Head>

      {loading ? (
        <Empty>Loading…</Empty>
      ) : visible.length === 0 ? (
        <>
          <Empty>No data collected for this client yet.</Empty>
          {addFieldUI}
        </>
      ) : (
        <>
        {orderedCats.map(cat => (
          <div key={cat}>
            <GroupTitle>{CATEGORY_LABELS[cat] || cat}</GroupTitle>
            {groups[cat].map(f => {
              const draft = drafts[f.key];
              const val = draft !== undefined ? draft : f.value;
              const err = errors[f.key];
              const hasOptions = Array.isArray(f.options) && f.options.length > 0;
              return (
                <Row key={f.key}>
                  <div>
                    <Label title={f.key}>
                      {f.label}
                      {(f.category === 'custom' || !f.known) && <CustomTag>custom</CustomTag>}
                      {(f.category === 'custom' || !f.known || revealed.has(f.key)) && (
                        <RmBtn title="Remove field" onClick={() => removeField(f)}>×</RmBtn>
                      )}
                    </Label>
                    {Array.isArray(f.usedBy) && f.usedBy.length > 0 && (
                      <UsedBy title={`Used by: ${f.usedBy.join(', ')}`}>
                        Used by: {f.usedBy.length <= 2 ? f.usedBy.join(', ') : `${f.usedBy.slice(0, 2).join(', ')} +${f.usedBy.length - 2}`}
                      </UsedBy>
                    )}
                  </div>
                  <ValueBox>
                    {hasOptions ? (
                      <ComboSelect
                        error={!!err}
                        value={val || ''}
                        options={f.options}
                        onChange={v => save(f, v)}
                      />
                    ) : (
                      <Input
                        $err={!!err}
                        type={f.type === 'date' ? 'text' : 'text'}
                        value={val || ''}
                        placeholder={f.type === 'date' ? 'YYYY-MM-DD' : ''}
                        onChange={e => setDrafts(prev => ({ ...prev, [f.key]: e.target.value }))}
                        onBlur={e => { if (e.target.value !== f.value) save(f, e.target.value); }}
                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                      />
                    )}
                    {err && <Err>{err}</Err>}
                    {savedKey === f.key && <Saved>✓ saved</Saved>}
                  </ValueBox>
                </Row>
              );
            })}
          </div>
        ))}
        {addFieldUI}
        </>
      )}
    </Wrap>
  );
};

export default ClientProfilePanel;
